import { useCompararPublicacoes } from '@/api/publicacoes';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { MensagemPublicacoes, vigenciaTexto } from './publicacoes-util';

export interface PublicacoesCompararDialogProps {
  /** Publicacao A e B (ids). Ambas nulas = fechado. */
  a: string | null;
  b: string | null;
  onOpenChange: (aberto: boolean) => void;
}

/** Comparacao preco a preco: as linhas e as diferencas vem prontas da API (so exibicao). "—" onde nao ha preco. */
export function PublicacoesCompararDialog({ a, b, onOpenChange }: PublicacoesCompararDialogProps) {
  const comparacao = useCompararPublicacoes(a, b);
  const dados = comparacao.data;

  return (
    <Dialog open={a !== null && b !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Comparar publicações</DialogTitle>
          {dados ? (
            <DialogDescription>
              A: {vigenciaTexto(dados.a)} · B: {vigenciaTexto(dados.b)}
            </DialogDescription>
          ) : (
            <DialogDescription>Preço a preço, da publicação A para a B.</DialogDescription>
          )}
        </DialogHeader>

        {comparacao.isPending && <MensagemPublicacoes papel="status">Comparando publicações…</MensagemPublicacoes>}
        {comparacao.isError && <ErroFormacao erro={comparacao.error} titulo="Não foi possível comparar as publicações" />}
        {dados && dados.linhas.length === 0 && <MensagemPublicacoes>As duas publicações não têm preços para comparar.</MensagemPublicacoes>}
        {dados && dados.linhas.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[720px] border-collapse text-small">
              <thead>
                <tr className="bg-surface-muted text-left text-caption text-ink-muted">
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 font-medium">Tabela</th>
                  <th className="px-3 py-2 font-medium">Condição</th>
                  <th className="px-3 py-2 text-right font-medium">Preço A</th>
                  <th className="px-3 py-2 text-right font-medium">Preço B</th>
                  <th className="px-3 py-2 text-right font-medium">Diferença</th>
                  <th className="px-3 py-2 text-right font-medium">Variação</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {dados.linhas.map((l) => (
                  <tr key={`${l.itemCodigo}-${l.tabelaCodigo}-${l.condicaoNome}`} data-testid="linha-comparacao" className="border-t border-line-soft">
                    <td className="px-3 py-2 font-sans">
                      <span className="font-medium">{l.itemCodigo}</span> <span className="text-ink-muted">{l.itemDescricao}</span>
                    </td>
                    <td className="px-3 py-2 font-sans">{l.tabelaCodigo}</td>
                    <td className="px-3 py-2 font-sans">{l.condicaoNome}</td>
                    <td className="px-3 py-2 text-right">{formatarMoeda(l.precoA)}</td>
                    <td className="px-3 py-2 text-right">{formatarMoeda(l.precoB)}</td>
                    <td className="px-3 py-2 text-right">{formatarMoeda(l.diferenca)}</td>
                    <td className="px-3 py-2 text-right">{formatarFracaoPercentual(l.variacao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
